import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type { FormEvent, TransitionEvent } from 'react'
import * as api from '../api'
import { ApiError } from '../api'
import type { Draft } from '../guests'
import { draftFrom, everyoneAnswered } from '../guests'
import type { Invite, RsvpResponse } from '../types'
import { LetterCourier } from '../animations/LetterCourier'
import { scrollIntoFrame, scrollIntoFrameThenSettle } from '../animations/scrollIntoFrame'
import type { DeliveryStage } from '../animations/useLetterDelivery'
import { useLetterDelivery } from '../animations/useLetterDelivery'
import { Confirmation } from './Confirmation'
import type { CardMotion } from './RsvpForm'
import { RsvpForm } from './RsvpForm'

/** What the card should be doing at each point in the send-off. */
function cardMotion(stage: DeliveryStage): CardMotion {
  switch (stage) {
    case 'idle':
      return 'none'
    case 'locking':
      return 'locking'
    // He is running in, but has not reached it yet - so it stays as it was
    // when it sealed.
    case 'approach':
      return 'locking'
    case 'impact':
      return 'shaking'
    case 'folding':
      return 'shrinking'
    case 'carrying':
    case 'gone':
      return 'gone'
  }
}

interface RsvpCardProps {
  invite: Invite
  onSaved: (invite: Invite) => void
  /** Called when the session turns out to have expired mid-edit. */
  onSessionLost: () => void
}

/**
 * Owns the RSVP: the draft answers, the submit sequence, and the swap from the
 * form to the confirmation. The markup for each of those two states lives in
 * RsvpForm and Confirmation.
 */
export function RsvpCard({ invite, onSaved, onSessionLost }: RsvpCardProps) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(invite))
  const [error, setError] = useState<string | null>(null)
  // A guest who already replied lands straight on the confirmation, rather than
  // being made to sit through the animation again.
  const [confirmed, setConfirmed] = useState(() => everyoneAnswered(invite))

  const cardRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const delivery = useLetterDelivery()

  // The card leaves the layout once the dog has it, which would let the rest of
  // the page snap upwards mid-animation. Pinning the height the card had holds
  // the space open until the confirmation is ready to take it, and then the
  // transition settles the two heights together instead of jumping.
  const [reservedHeight, setReservedHeight] = useState<number | null>(null)

  /**
   * Releases the pinned height and brings the confirmation into frame.
   *
   * The scroll has to wait for the whole send-off, which is why it lives here
   * rather than beside the swap the way the food summary's does. `play()`
   * measures the card's viewport rect once and holds it for the full sequence,
   * so the dog and the letter are positioned against that snapshot - move the
   * page while he is running and he barges empty space. By the time the height
   * has settled he has gone.
   *
   * It is worth doing at all because the card usually shrinks and the page grows
   * underneath it: saying yes reveals the food section. Measured on a 390x560
   * frame with three guests, the thank-you ended up 236px above the fold with
   * only its last 80px showing.
   *
   * Only ever called from the submit path. A guest who arrives already replied
   * has no pinned height, so the effect below returns early and nothing scrolls.
   */
  const settleHeight = useCallback(() => {
    setReservedHeight(null)
    if (contentRef.current) scrollIntoFrame(contentRef.current, 'center')
  }, [])

  useLayoutEffect(() => {
    if (!confirmed || reservedHeight === null || !contentRef.current) return

    const target = contentRef.current.offsetHeight
    if (target === reservedHeight) {
      // No transition would fire, so release the height now rather than leaving
      // it pinned forever waiting for a transitionend that never comes.
      settleHeight()
      return
    }
    // A frame's delay so the browser commits the old height first and has
    // something to animate from.
    const frame = requestAnimationFrame(() => setReservedHeight(target))
    return () => cancelAnimationFrame(frame)
    // Runs when the confirmation appears; reservedHeight is deliberately not a
    // dependency, or settling it would retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmed])

  const answeredAll = invite.people.every((p) => (draft[p.person_id] ?? 'waiting') !== 'waiting')
  // `preparing` covers the moment between pressing send and the sequence
  // starting, while the page is being brought into position. Without it a second
  // press would slip through, since the delivery is still idle.
  const [preparing, setPreparing] = useState(false)
  const busy = preparing || delivery.stage !== 'idle'

  function setResponse(personId: string, response: RsvpResponse) {
    setDraft((current) => ({ ...current, [personId]: response }))
    setError(null)
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy || !answeredAll) return

    setError(null)
    setPreparing(true)

    /*
     * Get the card into the frame, and wait for the page to stop, before any of
     * the send-off begins.
     *
     * play() takes the card's viewport rect once and the dog is positioned
     * against that snapshot for the whole sequence. Press send with the card
     * mostly above the fold - which is exactly where it is when the button is at
     * the top of the screen - and he runs his entire route up there, out of
     * sight. All the guest sees is the card twitch and then vanish.
     */
    if (cardRef.current) await scrollIntoFrameThenSettle(cardRef.current, 'center')
    setPreparing(false)

    // offsetHeight rather than getBoundingClientRect, so the fold's transform
    // cannot shrink the height we are about to hold open.
    if (cardRef.current) setReservedHeight(cardRef.current.offsetHeight)

    const people = invite.people.map((p) => ({
      person_id: p.person_id,
      response: draft[p.person_id] ?? 'waiting',
    }))

    // The animation and the request run together, and we wait for both. The
    // dog therefore always finishes its run, however fast or slow the API is.
    const [saved] = await Promise.allSettled([
      api.updateInvite(people),
      delivery.play(cardRef.current),
    ])

    if (saved.status === 'fulfilled') {
      onSaved(saved.value)
      setConfirmed(true)
      return
    }

    // Put the card back so the guest can try again.
    delivery.reset()
    setPreparing(false)
    setReservedHeight(null)
    const reason = saved.reason
    if (reason instanceof ApiError && reason.isUnauthenticated) {
      onSessionLost()
      return
    }
    setError(
      reason instanceof ApiError ? reason.message : 'We could not save your reply. Please try again.',
    )
  }

  function handleEditAgain() {
    setDraft(draftFrom(invite))
    setConfirmed(false)
    setError(null)
    delivery.reset()
    setReservedHeight(null)
    reopenedByGuest.current = true
  }

  /*
   * Bring the whole panel into view when the guest asks to change their answer.
   *
   * The form is taller than the confirmation it replaces, so its foot can end up
   * below the fold - and its foot is where the send button is. `nearest` makes
   * the smallest move that shows all of it and leaves the page alone if it is
   * already there; anything too tall for the frame is aligned to its top
   * instead, which is handled inside scrollIntoFrame.
   *
   * A ref rather than state for the flag: it only has to survive to the layout
   * effect, and it means a guest arriving on a card they already replied to does
   * not get scrolled around.
   */
  const reopenedByGuest = useRef(false)

  useLayoutEffect(() => {
    if (confirmed || !reopenedByGuest.current || !contentRef.current) return
    reopenedByGuest.current = false
    scrollIntoFrame(contentRef.current, 'nearest')
  }, [confirmed])

  // Once the height has finished settling onto the confirmation, hand control
  // back to the content so later changes reflow normally.
  function handleHeightSettled(event: TransitionEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget && event.propertyName === 'height') {
      settleHeight()
    }
  }

  return (
    <>
      <LetterCourier stage={delivery.stage} origin={delivery.origin} />

      <div
        className="transition-[height] duration-500 ease-out motion-reduce:transition-none"
        style={reservedHeight === null ? undefined : { height: reservedHeight, overflow: 'hidden' }}
        onTransitionEnd={handleHeightSettled}
      >
        <div ref={contentRef}>
          {confirmed ? (
            <Confirmation invite={invite} onEditAgain={handleEditAgain} />
          ) : (
            <RsvpForm
              ref={cardRef}
              invite={invite}
              draft={draft}
              busy={busy}
              answeredAll={answeredAll}
              error={error}
              motion={cardMotion(delivery.stage)}
              onChange={setResponse}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </>
  )
}
